import React from "react";
import { cn } from "../../../../../../helpers";
import { useDataMan } from "../../hooks/useDataMan";
import { useDataManRow } from "../../hooks/useDataManRow";
import { DataManCellProvider } from "../../providers/DataManCellProvider";
import { DataManColumnProvider } from "../../providers/DataManColumnProvider";
import { DataManRowProvider } from "../../providers/DataManRowProvider";
import { DataManColumDef } from "../../types/columns";
import { DataManRowActions } from "../common/DataManRowActions";
import { DataManSelectControl } from "../common/DataManSelectControl";

interface CompactCellValueProps {
  column: DataManColumDef<unknown>;
  value: unknown;
}

const CompactCellValue: React.FC<CompactCellValueProps> = ({
  column,
  value
}) => {
  const RendererComponent = column?.renderer;

  // If column has a custom renderer component, use it with providers
  if (RendererComponent) {
    return (
      <DataManColumnProvider column={column}>
        <DataManCellProvider value={value}>
          <RendererComponent />
        </DataManCellProvider>
      </DataManColumnProvider>
    );
  }

  // Handle React elements (returned from column.render)
  if (React.isValidElement(value)) {
    return <span className="text-neutral-900 text-sm">{value}</span>;
  }

  // Handle primitives
  if (typeof value === "string") {
    return <span className="text-neutral-900 text-sm">{value}</span>;
  }

  if (typeof value === "number") {
    return <span className="text-neutral-900 text-sm">{value}</span>;
  }

  if (typeof value === "boolean") {
    return (
      <span className="text-neutral-900 text-sm">{value ? "Yes" : "No"}</span>
    );
  }

  // Handle null/undefined
  if (value === null || value === undefined) {
    return <span className="text-neutral-400 text-sm">-</span>;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return <span className="text-neutral-900 text-sm">{value.join(", ")}</span>;
  }

  // Handle objects with toString
  if (typeof value === "object" && value !== null) {
    const stringValue = String(value);
    if (stringValue !== "[object Object]") {
      return <span className="text-neutral-900 text-sm">{stringValue}</span>;
    }
  }

  return <span className="text-neutral-400 text-sm">-</span>;
};

interface DataManCompactViewRecordInnerProps {
  isFirst: boolean;
  isLast: boolean;
}

const DataManCompactViewRecordInner: React.FC<
  DataManCompactViewRecordInnerProps
> = ({ isFirst, isLast }) => {
  const {
    visibleColumns,
    hasActions,
    hasBulkSelection,
    clickRecord,
    isItemSelected,
    dataIdKey = "id",
    showSelectedBackground
  } = useDataMan();
  const { data } = useDataManRow();

  const itemId = String(data?.[dataIdKey] || "");
  const isSelected = isItemSelected(itemId);

  const handleClick = () => {
    clickRecord(data);
  };

  return (
    <div
      className={cn(
        "flex flex-col border border-neutral-200 bg-white",
        isFirst && "rounded-t-lg",
        isLast && "rounded-b-lg",
        !isFirst && "-mt-px",
        isSelected && showSelectedBackground && "bg-blue-50",
        "cursor-pointer transition-colors hover:bg-neutral-50"
      )}
      onClick={handleClick}
    >
      {/* Selection checkbox at top if bulk selection is enabled */}
      {hasBulkSelection && (
        <div
          className="flex items-center border-neutral-100 border-b px-4 py-2"
          onClick={(e) => e.stopPropagation()}
        >
          <DataManSelectControl />
        </div>
      )}

      {/* Column data as label-value pairs */}
      <div className="flex flex-col divide-y divide-neutral-100">
        {visibleColumns?.map((column) => {
          // Match table view value resolution: use render function result, or fall back to raw data
          const value = column.render?.(data) ?? data?.[column.key];

          return (
            <div key={column.key} className="flex px-4 py-3">
              <div className="w-24 shrink-0 font-medium text-neutral-500 text-sm">
                {column.title}
              </div>
              <div className="min-w-0 flex-1 break-all">
                <CompactCellValue column={column} value={value} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions at the bottom */}
      {hasActions && (
        <div
          className="flex items-center justify-end border-neutral-200 border-t bg-neutral-50 px-4 py-3"
          onClick={(e) => e.stopPropagation()}
        >
          <DataManRowActions />
        </div>
      )}
    </div>
  );
};

export interface DataManCompactViewRecordProps {
  // biome-ignore lint/suspicious/noExplicitAny: data can be any shape
  data: any;
  index: number;
  totalCount: number;
}

export const DataManCompactViewRecord: React.FC<
  DataManCompactViewRecordProps
> = ({ data, index, totalCount }) => {
  const isFirst = index === 0;
  const isLast = index === totalCount - 1;

  return (
    <DataManRowProvider data={data}>
      <DataManCompactViewRecordInner isFirst={isFirst} isLast={isLast} />
    </DataManRowProvider>
  );
};
